/**
 * Reports — parent shell cho nhóm report (dashboard), tổ chức theo đúng pattern
 * `SaleContractPage` (/sales/contract): 1 parent page + Tabs đồng bộ URL, mỗi tab
 * render 1 dashboard view. Sidebar children + in-page tab dùng chung URL.
 *
 * Routes (App.tsx): `report` (element=ReportsPage) + child `element={null}` cho
 * từng segment (pnl / futures-position / …) + index redirect → pnl.
 */
import { useEffect, useMemo, useState } from 'react'
import { Box, Card, CardContent, CircularProgress, Tab, Tabs, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'

import { usePreference } from '@/hooks/usePreference'
import { CARD_CONTENT_COMPACT } from '../../theme/responsive'
import { ReportToolbarProvider } from './reportToolbarSlot'
import PnLDashboardPage from '../dashboard/pnl/PnLDashboardPage'
import FuturesPositionPage from '../dashboard/futuresPosition/FuturesPositionPage'
import RiskManagementPage from '../dashboard/riskManagement/RiskManagementPage'
import LongShortDashboardPage from '../dashboard/longShort/LongShortDashboardPage'
import InventoryDashboardPage from '../dashboard/inventory/InventoryDashboardPage'
import CashflowsDashboardPage from '../dashboard/cashflows/CashflowsDashboardPage'

const TAB_CONFIG = [
  { path: 'pnl', label: 'P&L' },
  { path: 'futures-position', label: 'Futures Position' },
  { path: 'risk-management', label: 'Risk Management' },
  { path: 'long-short', label: 'Long/Short' },
  { path: 'inventory', label: 'Inventory' },
  { path: 'cashflows', label: 'Cash Flows' },
] as const

/** Valid-tab guard — true nếu `path` là 1 sub-tab hợp lệ của Dashboards. */
const isValidTab = (path: string): boolean => TAB_CONFIG.some((t) => t.path === path)

// Tab sx trích từ SaleContractPage (cùng tone toàn app).
const tabsSx = {
  borderBottom: 1,
  borderColor: 'divider',
  minHeight: 34,
  '& .MuiTabs-indicator': { height: 2.5, borderRadius: '3px 3px 0 0' },
  '& .MuiTab-root': {
    position: 'relative',
    minHeight: 34,
    minWidth: 0,
    py: 0.25,
    px: 1.5,
    fontSize: '0.78rem',
    fontWeight: 600,
    letterSpacing: 0.15,
    textTransform: 'none',
    color: 'text.secondary',
    borderRadius: '6px 6px 0 0',
    transition: 'color 140ms ease, background-color 140ms ease',
    '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
    '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
    '&:not(:last-of-type)::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      height: 16,
      width: '1px',
      bgcolor: 'divider',
      opacity: 0.6,
      transition: 'opacity 140ms ease',
    },
    '&:hover::after, &.Mui-selected::after': { opacity: 0 },
  },
} as const

export default function ReportsPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const segment = useMemo(
    () => location.pathname.split('/').pop() ?? '',
    [location.pathname],
  )

  const activeIndex = useMemo(() => {
    const idx = TAB_CONFIG.findIndex((t) => t.path === segment)
    return idx >= 0 ? idx : 0
  }, [segment])

  const current = TAB_CONFIG[activeIndex]

  // Per-user "last opened tab" qua generic preferences registry (mirror Factory
  // navPrefs, nhưng qua usePreference thay vì slice riêng).
  const navPref = usePreference<{ lastTab: string }>('dashboard', 'navigation', undefined, { refetchOnMount: true })

  // Ghi nhớ SECTION = Dashboards để `/report` lần sau mở lại đúng section này
  // (xem ReportIndexRedirect). Section-pointer server-side qua generic registry.
  const sectionPref = usePreference<{ lastSection: string }>('report', 'navigation', undefined, { refetchOnMount: true })
  useEffect(() => {
    if (sectionPref.isLoading || sectionPref.isFetching) return
    if (sectionPref.value?.lastSection === 'dashboards') return
    void sectionPref.patch({ lastSection: 'dashboards' }).catch(() => undefined)
  }, [sectionPref.isLoading, sectionPref.isFetching, sectionPref.value?.lastSection, sectionPref.patch])

  // Bare route (URL không có sub-tab hợp lệ, vd `/report/dashboards`) → restore tab
  // cuối user đã mở thay vì luôn dùng default. Chờ pref fetch xong; fallback tab đầu.
  useEffect(() => {
    if (isValidTab(segment)) return
    if (navPref.isLoading || navPref.isFetching) return
    const saved = navPref.value?.lastTab
    const target = saved && isValidTab(saved) ? saved : TAB_CONFIG[0].path
    navigate(`/report/dashboards/${target}`, { replace: true })
  }, [segment, navigate, navPref.isLoading, navPref.isFetching, navPref.value])

  // Auto-save tab hiện tại (debounce 400ms) để lần sau vào bare route restore đúng.
  useEffect(() => {
    if (navPref.isLoading || navPref.isFetching) return
    if (!isValidTab(segment)) return
    if (navPref.value?.lastTab === segment) return
    void navPref.patch({ lastTab: segment }).catch(() => undefined)
  }, [segment, navPref.value?.lastTab, navPref.patch])

  // Block header chung (giống PageHeader của SaleContractPage): mỗi dashboard
  // portal filter + reload lên đây qua ReportToolbarProvider. Gate render dashboard
  // tới khi slot sẵn sàng → tránh nháy header render inline rồi mới portal.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)

  // Resolve-first: URL chưa có sub-tab hợp lệ (đang đọc DB + chờ restore) → hiện
  // loading, KHÔNG render dashboard default. Có tab đúng trên URL mới render.
  if (!isValidTab(location.pathname.split('/').filter(Boolean).pop() ?? ''))
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={24} />
      </Box>
    )

  return (
    // `<main>` là display:block (không flex) → flex:1 vô tác dụng. Dùng height:100%
    // (giống ReportValuePage + các dashboard standalone) để khoá chuỗi height:
    // dashboard `height:100%`/`{lg:'100%'}` mới resolve → bảng fill + scroll nội bộ
    // (Futures/Risk/Long-Short/Inventory hết trống/sai scroll).
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Common header block — nguyên header (title + filter + reload) của tab đang
          chọn portal vào đây, nằm trên tabs (giống PageHeader của SaleContractPage).
          Ẩn khi rỗng. */}
      <Box ref={setSlot} sx={{ mb: 0.75, '&:empty': { display: 'none' } }} />

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => {
            void navPref.patch({ lastTab: TAB_CONFIG[value].path }).catch(() => undefined)
            navigate(`/report/dashboards/${TAB_CONFIG[value].path}`)
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={tabsSx}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>
        {/* Report area scrolls vertically so fixed-height dashboard cards are
            never cut off on smaller devices (fit-to-device + scroll). */}
        <CardContent sx={{ ...CARD_CONTENT_COMPACT, overflowY: 'auto', overflowX: 'hidden' }}>
          <ReportToolbarProvider container={slot}>
            {!slot ? null : current.path === 'pnl' ? (
              <PnLDashboardPage />
            ) : current.path === 'futures-position' ? (
              <FuturesPositionPage />
            ) : current.path === 'risk-management' ? (
              <RiskManagementPage />
            ) : current.path === 'long-short' ? (
              <LongShortDashboardPage />
            ) : current.path === 'inventory' ? (
              <InventoryDashboardPage />
            ) : current.path === 'cashflows' ? (
              <CashflowsDashboardPage />
            ) : (
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Select a report.
                </Typography>
              </Box>
            )}
          </ReportToolbarProvider>
        </CardContent>
      </Card>
    </Box>
  )
}
